// State Data console — HTTP client.
//
// Same apiFetch pattern as RegistryControlPlane/api.js: getUser() -> Bearer token ->
// REACT_APP_INTEGRATION_MICROSERVICE_URL. Self-contained rather than shared, matching
// how the other Implementations tabs are written.

import { getUser } from '../../../auth/authService';

const INTEGRATION_URL = process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL;
const API_BASE = `${INTEGRATION_URL}/state-data`;

async function apiFetch(path, options = {}) {
    const user = await getUser();
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`,
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const text = await res.text();
        let detail;
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
        throw new Error(detail || `HTTP ${res.status}`);
    }
    return res.json();
}

// GET /state-data/status -> { generated_at, states: [...] }
export const getStateStatus = () => apiFetch('/status');

// POST /state-data/status/refresh -> { status: 'queued' | 'running', job }
// The snapshot is only ever written by a scan, and the scan takes minutes (every state
// folder, every client container, the registration tables), so the server runs it as a
// job in its own pod. 'running' means a scan was already under way and this joined it.
export const refreshStateStatus = () => apiFetch('/status/refresh', { method: 'POST' });

// GET /state-data/status/refresh/{name} -> { phase, log, result, reason, message }
export const getStatusRefreshJob = (name) =>
    apiFetch(`/status/refresh/${encodeURIComponent(name)}`);

// GET /state-data/enrollment -> { states: { <state>: { enrolled, eligible, blocked } } }
// Separate from the status snapshot because it carries client ids and is read live:
// enrolling changes it, and a page that kept showing the pre-enrolment snapshot would
// invite someone to enrol the same district twice.
// The server caches the SharePoint listing of each state's outputs for ten minutes; the
// panel's refresh button passes refresh=true to relist. The client half is always live.
export const getEnrollment = (refresh = false) =>
    apiFetch(refresh ? '/enrollment?refresh=true' : '/enrollment');

// POST /state-data/enroll — phases 2-4 for one client, no state rebuild.
// dryRun defaults true on the server; pass false deliberately.
// A real enrol answers { status: 'queued', job } : the server does the database steps
// and hands the copy to a job in its own pod (the copy took the API down once), so the
// caller polls getEnrolJob(job) until its phase is 'succeeded' or 'failed'.
export const enrollClient = (state, clientId, dryRun = true) =>
    apiFetch('/enroll', {
        method: 'POST',
        body: JSON.stringify({ state, client_id: clientId, dry_run: dryRun }),
    });

// GET /state-data/enroll/jobs/{name} -> { phase, log, result, reason, message }
export const getEnrolJob = (name) => apiFetch(`/enroll/jobs/${encodeURIComponent(name)}`);
